import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import StarIcon from '@mui/icons-material/Star';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

const SpecialItemDetailsDialog = ({
  open,
  onClose,
  businessDate,
  storeId,
  glAccount,
  memoDetail,
  firebaseUser,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [showOrderGuid, setShowOrderGuid] = useState(false);
  const [showCheckGuid, setShowCheckGuid] = useState(false);
  const [orderBy, setOrderBy] = useState('grossSale');
  const [order, setOrder] = useState('desc');
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});

  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const handleCopy = () => {
    setCopySnackbarOpen(true);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredDetails = useMemo(() => {
    if (!details.length) return details;
    const activeFilters = Object.entries(columnFilters).filter(([_, value]) => value.trim() !== '');
    if (activeFilters.length === 0) return details;
    return details.filter((row) => {
      return activeFilters.every(([columnId, filterValue]) => {
        const cellValue = String(row[columnId] ?? '').toLowerCase();
        const filterLower = filterValue.toLowerCase();
        return cellValue.includes(filterLower);
      });
    });
  }, [details, columnFilters]);

  const sortedDetails = useMemo(() => {
    if (!filteredDetails.length) return filteredDetails;

    return [...filteredDetails].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle numeric sorting for amounts and order number
      if (['grossSale', 'netSale', 'orderNumber'].includes(orderBy)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        // String sorting
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDetails, orderBy, order]);

  useEffect(() => {
    if (open && businessDate && storeId && glAccount) {
      fetchDetails();
    }
  }, [open, businessDate, storeId, glAccount]);

  const fetchDetails = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError(null);

      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      const response = await axios.get('/hd-api/gl-export/special-item-details', {
        params: {
          businessDate,
          storeId,
          glAccount,
        },
        headers: {
          Authorization: `Bearer ${firebaseUserIdToken}`,
        },
      });

      setDetails(response.data.data || []);
    } catch (err) {
      console.error('Error fetching special item details:', err);
      setError('Failed to load special item details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate totals
  const totals = useMemo(() => {
    return filteredDetails.reduce(
      (acc, d) => ({
        grossSale: acc.grossSale + (parseFloat(d.grossSale) || 0),
        netSale: acc.netSale + (parseFloat(d.netSale) || 0),
      }),
      { grossSale: 0, netSale: 0 }
    );
  }, [filteredDetails]);

  const hasActiveFilters = Object.values(columnFilters).some((v) => v.trim() !== '');

  // Count by type
  const typeCounts = useMemo(() => {
    return filteredDetails.reduce(
      (acc, d) => {
        if (d.isDispatchTip) acc.dispatchTip++;
        if (d.isEzCaterFee) acc.ezCaterFee++;
        if (!d.isDispatchTip && !d.isEzCaterFee) acc.other++;
        return acc;
      },
      { dispatchTip: 0, ezCaterFee: 0, other: 0 }
    );
  }, [filteredDetails]);

  // Export to Excel
  const handleExport = () => {
    const exportData = sortedDetails.map((item) => ({
      'Order #': item.orderNumber,
      'Item': item.itemName,
      'Type': item.orderType,
      'Dispatch Tip': item.isDispatchTip ? 'Yes' : 'No',
      'ezCater Fee': item.isEzCaterFee ? 'Yes' : 'No',
      'Gross Sale': parseFloat(item.grossSale) || 0,
      'Net Sale': parseFloat(item.netSale) || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Special Items');
    XLSX.writeFile(wb, `special-items-${storeId}-${businessDate}.xlsx`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StarIcon color="warning" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            Special Item Details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            Store {storeId} - {businessDate} - GL {glAccount}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {typeCounts.dispatchTip > 0 && (
            <Chip
              icon={<LocalShippingIcon sx={{ fontSize: 16 }} />}
              label={`${typeCounts.dispatchTip} Dispatch Tip${typeCounts.dispatchTip !== 1 ? 's' : ''}`}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
          {typeCounts.ezCaterFee > 0 && (
            <Chip
              icon={<RestaurantIcon sx={{ fontSize: 16 }} />}
              label={`${typeCounts.ezCaterFee} ezCater Fee${typeCounts.ezCaterFee !== 1 ? 's' : ''}`}
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}
          {typeCounts.other > 0 && (
            <Chip
              label={`${typeCounts.other} Other`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && details.length === 0 && (
          <Alert severity="info">
            No special item details found for this selection.
          </Alert>
        )}

        {!loading && !error && details.length > 0 && (
          <>
            <TableContainer sx={{ maxHeight: 400, userSelect: 'text' }} onCopy={handleCopy}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {showOrderGuid && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                        <TableSortLabel
                          active={orderBy === 'orderGuid'}
                          direction={orderBy === 'orderGuid' ? order : 'asc'}
                          onClick={() => handleRequestSort('orderGuid')}
                        >
                          Order GUID
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {showCheckGuid && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                        <TableSortLabel
                          active={orderBy === 'checkGuid'}
                          direction={orderBy === 'checkGuid' ? order : 'asc'}
                          onClick={() => handleRequestSort('checkGuid')}
                        >
                          Check GUID
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'orderNumber'}
                        direction={orderBy === 'orderNumber' ? order : 'asc'}
                        onClick={() => handleRequestSort('orderNumber')}
                      >
                        Order #
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'itemName'}
                        direction={orderBy === 'itemName' ? order : 'asc'}
                        onClick={() => handleRequestSort('itemName')}
                      >
                        Item
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'orderType'}
                        direction={orderBy === 'orderType' ? order : 'asc'}
                        onClick={() => handleRequestSort('orderType')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="center">
                      Flags
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'grossSale'}
                        direction={orderBy === 'grossSale' ? order : 'asc'}
                        onClick={() => handleRequestSort('grossSale')}
                      >
                        Gross Sale
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'netSale'}
                        direction={orderBy === 'netSale' ? order : 'asc'}
                        onClick={() => handleRequestSort('netSale')}
                      >
                        Net Sale
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    {showOrderGuid && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                        <TextField
                          size="small"
                          placeholder="Filter..."
                          value={columnFilters.orderGuid || ''}
                          onChange={(e) => handleColumnFilterChange('orderGuid', e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterListIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters.orderGuid && (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => clearColumnFilter('orderGuid')}
                                  edge="end"
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ width: '100%' }}
                        />
                      </TableCell>
                    )}
                    {showCheckGuid && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                        <TextField
                          size="small"
                          placeholder="Filter..."
                          value={columnFilters.checkGuid || ''}
                          onChange={(e) => handleColumnFilterChange('checkGuid', e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterListIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters.checkGuid && (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => clearColumnFilter('checkGuid')}
                                  edge="end"
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ width: '100%' }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.orderNumber || ''}
                        onChange={(e) => handleColumnFilterChange('orderNumber', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.orderNumber && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('orderNumber')}
                                edge="end"
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.itemName || ''}
                        onChange={(e) => handleColumnFilterChange('itemName', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.itemName && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('itemName')}
                                edge="end"
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.orderType || ''}
                        onChange={(e) => handleColumnFilterChange('orderType', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.orderType && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('orderType')}
                                edge="end"
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }} align="center">
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }} align="right">
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.grossSale || ''}
                        onChange={(e) => handleColumnFilterChange('grossSale', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.grossSale && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('grossSale')}
                                edge="end"
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }} align="right">
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.netSale || ''}
                        onChange={(e) => handleColumnFilterChange('netSale', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.netSale && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('netSale')}
                                edge="end"
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDetails.map((detail, index) => (
                    <TableRow
                      key={`${detail.orderGuid}-${detail.checkGuid}-${index}`}
                      sx={{
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                      }}
                    >
                      {showOrderGuid && (
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {detail.orderGuid || '-'}
                        </TableCell>
                      )}
                      {showCheckGuid && (
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {detail.checkGuid || '-'}
                        </TableCell>
                      )}
                      <TableCell>{detail.orderNumber || '-'}</TableCell>
                      <TableCell>{detail.itemName || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={detail.orderType || 'Unknown'}
                          size="small"
                          color={detail.orderType === 'Catering' ? 'success' : 'warning'}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {detail.isDispatchTip && (
                            <Tooltip title="Dispatch Tip">
                              <LocalShippingIcon sx={{ fontSize: 18, color: 'info.main' }} />
                            </Tooltip>
                          )}
                          {detail.isEzCaterFee && (
                            <Tooltip title="ezCater Fee">
                              <RestaurantIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                            </Tooltip>
                          )}
                          {!detail.isDispatchTip && !detail.isEzCaterFee && (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(detail.grossSale)}</TableCell>
                      <TableCell align="right">{formatCurrency(detail.netSale)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                pt: 2,
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredDetails.length} item{filteredDetails.length !== 1 ? 's' : ''}
                  {hasActiveFilters && ` (filtered from ${details.length})`}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showOrderGuid}
                      onChange={(e) => setShowOrderGuid(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Show Order GUID</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showCheckGuid}
                      onChange={(e) => setShowCheckGuid(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Show Check GUID</Typography>}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2.5 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Gross Sale
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatCurrency(totals.grossSale)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Net Sale
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(totals.netSale)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={details.length === 0}
        >
          Export
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default SpecialItemDetailsDialog;
